import { useEffect } from 'react';

import { useKeyMaster } from '../contexts/KeyMaster';

const isEditableElement = (element) => {
  if (!(element instanceof HTMLElement)) return false;
  return (
    element.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName) || element.getAttribute('role') === 'textbox'
  );
};

const getClipboardFiles = (event) => {
  const clipboardFiles = Array.from(event.clipboardData?.files || []);
  if (clipboardFiles.length) return clipboardFiles;

  return Array.from(event.clipboardData?.items || [])
    .filter((item) => item.kind === 'file')
    .map((item) => item.getAsFile())
    .filter(Boolean);
};

// Small shared hook specifically for handling image pastes everywhere but the composer.
//
// keychainId (optional): pass a KEYCHAIN value if your surface registers with KeyMaster.
// When set, we only grab pastes while that surface is the unlocked one — handy for
// page-wide pastes (e.g. settings pages) where focus is usually on body. Leave it off
// and we'll only catch pastes when focus is actually inside rootRef, so you don't
// accidentally hijack pastes meant for somewhere else on the page.
const useScopedClipboardFiles = ({ rootRef, onFiles, isEnabled = true, keychainId = null }) => {
  const { unlocked } = useKeyMaster();

  useEffect(() => {
    if (!isEnabled) return undefined;

    const handlePaste = (event) => {
      if (event.defaultPrevented) return;
      // When a KeyMaster layer is provided, only paste into the surface that currently owns focus.
      // Passing null keeps this usable for plain non-KeyMaster views too.
      if (keychainId && !unlocked(keychainId)) return;

      const root = rootRef?.current;
      if (!root) return;

      const files = getClipboardFiles(event);
      if (!files.length) return;

      const activeElement = document.activeElement;
      const targetNode = event.target instanceof Node ? event.target : null;
      const hasFocusInsideRoot =
        (activeElement instanceof Node && root.contains(activeElement)) || (targetNode && root.contains(targetNode));

      // Without a keychainId, KeyMaster can't tell us this surface is the "current" one,
      // so we'd otherwise hijack pastes anywhere on the page that aren't aimed at another input.
      // Require focus to actually be inside our root in that case.
      if (!keychainId && !hasFocusInsideRoot) return;

      if (!hasFocusInsideRoot && isEditableElement(activeElement)) return;

      event.preventDefault();
      onFiles(files);
    };

    document.addEventListener('paste', handlePaste);
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [isEnabled, keychainId, onFiles, rootRef, unlocked]);
};

export default useScopedClipboardFiles;
